<script setup lang="ts">
import DialogLayout from '@/components/common/DialogLayout.vue';
import { Contact } from '@/models/dto/ContactGroup';
import { ContactAction, ContactType, getContactServiceCall } from './utils/helpers';
import { computed, getCurrentInstance, ref } from 'vue';
import alert from "@/store/modules/alert";

const { proxy } = getCurrentInstance()!;
const t = proxy.$t.bind(proxy);

const props = defineProps<{
  value: boolean;
  contactToRemove: Contact;
  contactType: ContactType
  entityId: number
}>();

const emit = defineEmits(['cancel', 'confirm', 'refresh', 'update:value']);

const isSubmitting = ref<boolean>(false);

const cancelRemoveContact = () => {
    emit('update:value', false);
    emit('cancel');
}

const confirmRemoveContact = async () => {
  isSubmitting.value = true;
  const removeContactFn = getContactServiceCall(props.contactType, ContactAction.Remove)
  try {
    await removeContactFn(props.entityId, props.contactToRemove.externalId!);
    alert.add({
      text: "",
      color: "success",
      title: "Removed",
      dismissible: true,
    });
    emit('refresh');
  } catch (error: any) {
    console.error(`Removing Contact ${props.contactToRemove.externalId} from reservationDetailContactInfo reservation: ${props.entityId} failed: ${error.response.data.message}`);
    alert.add({
      text: "",
      color: "error",
      title: "Delete failed",
      dismissible: true,
    });
  } finally {
    emit('update:value', false);
    isSubmitting.value = false;
    emit('confirm');
  }
}

const removeConfirmationText = computed(() => {
  const name = props.contactToRemove?.name || props.contactToRemove?.email || ''
  return props.contactType === ContactType.Reservation
    ? t('tripContact.REMOVE_CONFIRMATION_RESERVATION', { name })
    : t('tripContact.REMOVE_CONFIRMATION_QUOTE', { name })
})

</script>

<template>
<DialogLayout
  v-if="props.value"
  :value="props.value"
  :title="$t('tripContact.REMOVE_CONTACT').toString()"
  @input="emit('update:value', $event)"
  override-fullscreen
  @click:header-action-right="cancelRemoveContact"
  prevent-default-header-close
>
    <p>{{ removeConfirmationText }}</p>
  <template #footer>
    <div class="d-flex justify-end align-center" style="gap: 8px;">
      <v-btn
        small
        text
        @click="cancelRemoveContact"
      >
        {{ $t('common.CANCEL') }}
      </v-btn>
      <v-btn
        small
        :disabled="isSubmitting"
        :loading="isSubmitting"
        color="red"
        outlined
        class="border-1"
        @click="confirmRemoveContact"
      >
        {{ $t('common.REMOVE') }}
      </v-btn>
    </div>
  </template>
</DialogLayout>
</template>
